import { type ListenerEffectAPI } from '@reduxjs/toolkit'
import { getAuth } from 'firebase/auth'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { DocxExportNoticePhase, type DocxExportNotice } from '@/agent-v2/docx-export/schemas'
import { AgentV2ServerEventType, type AgentV2ConversationDetailResponse } from '@/agent-v2/store/schemas'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { GlobalToastType, kToastDurationLongMs } from '@/constants/constants-ui'
import { matterPaths } from '@/routing/paths'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import type { AppDispatch, RootState } from '@/store/store'

/**
 * An export's result is a card in its chat. When an export this tab was tracking ends while that chat
 * is not on screen, say so with a toast, and mark another chat unread as a finished answer does.
 */
export function notifySettledDocxExports(
  previous: AgentV2ConversationDetailResponse | undefined,
  detail: AgentV2ConversationDetailResponse,
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>
) {
  const stillOpen = new Set((detail.open_docx_exports ?? []).map((openExport) => openExport.id))
  const settledIds = new Set((previous?.open_docx_exports ?? []).filter((openExport) => !stillOpen.has(openExport.id)).map((openExport) => openExport.id))
  if (!settledIds.size) return

  const conversationId = detail.conversation.id
  const { visibleChatConversationId, activeConversationId } = listenerApi.getState().chatUxState
  // Only the chat's creator can export, so collaborators reading along are not told about it.
  if (visibleChatConversationId === conversationId || detail.conversation.created_by_user_id !== getAuth().currentUser?.uid) return

  const notices = settledNotices(detail, settledIds)
  if (!notices.length) return
  // One toast at a time: the latest result is the one to show.
  listenerApi.dispatch(openGlobalToast(toastFor(notices[notices.length - 1], detail.conversation.matter_id, conversationId)))
  if (activeConversationId !== conversationId) listenerApi.dispatch(AgentV2ConversationsActions.markResponseUnread({ conversationId }))
}

function settledNotices(detail: AgentV2ConversationDetailResponse, exportIds: Set<string>): DocxExportNotice[] {
  const notices: DocxExportNotice[] = []
  for (const turn of detail.turns) {
    for (const event of turn.events) {
      if (event.type !== AgentV2ServerEventType.ASSISTANT_MESSAGE_CREATED) continue
      const notice = event.payload.docx_export
      if (notice && notice.phase !== DocxExportNoticePhase.STARTED && exportIds.has(notice.export_id)) notices.push(notice)
    }
  }
  return notices
}

function toastFor(notice: DocxExportNotice, matterId: string, conversationId: string) {
  if (notice.phase === DocxExportNoticePhase.READY) {
    return {
      type: GlobalToastType.SUCCESS,
      message: `${notice.file_name ?? `${notice.draft_title}.docx`} is ready`,
      durationMs: kToastDurationLongMs,
      withLink: notice.folder_id ? { label: 'Open in Matter Files', href: matterPaths.userFiles.folder(matterId, notice.folder_id) } : undefined,
    }
  }
  return {
    type: GlobalToastType.ERROR,
    message: `Couldn't export ${notice.draft_title}`,
    durationMs: kToastDurationLongMs,
    withLink: { label: 'Open chat', href: buildConversationPath(matterId, conversationId) },
  }
}
